import { Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";

type Option = {
  label: string;
  value: string;
};

type LabeledSelectBoxProps = {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
};

const normalizeForSearch = (value: string) =>
  value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

const LabeledSelectBoxAuto = (props: LabeledSelectBoxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(props.value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(props.options || []);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedValue(props.value || '');
  }, [props.value]);

  useEffect(() => {
    setFilteredOptions(props.options || []);
  }, [props.options]);

  // 검색어에 따라 옵션 필터링
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(props.options || []);
    } else {
      const normalizedSearchTerm = normalizeForSearch(searchTerm);
      const filtered = props.options?.filter(option =>
        normalizeForSearch(option.label).includes(normalizedSearchTerm)
      ) || [];
      setFilteredOptions(filtered);
    }
  }, [searchTerm, props.options]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getLabelByValue = (value: string) => {
    if (!value) return null;
    const matched = props.options?.find?.((opt) => opt.value === value);
    return matched?.label ?? null;
  };

  const handleSelectboxClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // 드롭다운을 열 때 input에 포커스
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setSearchTerm('');
    }
  };

  const handleOptionClick = (option: Option) => {
    setSelectedValue(option.value);
    props.onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <Typography sx={{ fontWeight: 400, fontSize: '12px', color: '#8A8A8A' }}>
        {props.placeholder}
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            padding: '12px 8px 13px 8px',
            borderBottom: '1px solid #2A2A2A',
            backgroundImage: 'url("/images/icon-arr-select.png")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            position: 'relative',
          }}
        >
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="검색..."
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontWeight: '600',
                fontSize: '16px',
                padding: 0,
              }}
            />
          ) : (
            <div 
              onClick={handleSelectboxClick}
              style={{ 
                width: '100%', 
                fontWeight: '600', 
                fontSize: '16px',
                cursor: 'pointer',
              }}
            >
              {getLabelByValue(selectedValue) ?? '전체'}
            </div>
          )}
        </div>

        {/* Dropdown options */}
        <ul 
          className="select_ul"
          style={{
            width: '100%',
            maxHeight: isOpen ? '300px' : '0px',
            overflowY: 'auto',
            overflowX: 'hidden',
            background: "#fff",
            backgroundColor: '#fff',
            borderRadius: '20px',
            boxShadow: '0 5px 10px rgba(0, 0, 0, 0.2)',
            marginTop: '10px',
            // paddingLeft: '10px',
            padding: 0,
            listStyle: 'none',
            transition: 'max-height 0.5s ease',
            zIndex: 2,
          }}
        >
          <style>
            {`
              .select_ul::-webkit-scrollbar {
                width: 10px;
              }
              .select_ul::-webkit-scrollbar-thumb {
                height: 30%;
                background: #d1d1d1;
                padding: 10px 0;
                margin:100px;
                border-radius: 20px;
                border: 12px solid transparent;
              }
              .select_ul::-webkit-scrollbar-track {
                border: 4px solid transparent;
                border-radius: 20px;
              }
            `}
          </style>

          {filteredOptions.length > 0 ? (
            filteredOptions.map((option: Option, index: number) => (
              <li
                key={`option-${index}`}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '2px 10px',
                  height: '40px',
                  fontWeight: '600', 
                  fontSize: '16px',
                  cursor: 'pointer',
                  backgroundColor: selectedValue === option.value ? '#f0f0f0' : 'transparent',
                }}
                onClick={() => handleOptionClick(option)}
                onMouseEnter={(e) => {
                  (e.target as HTMLLIElement).style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLLIElement).style.backgroundColor = 
                    selectedValue === option.value ? '#f0f0f0' : 'transparent';
                }}
              >
                {option.label}
              </li>
            ))
          ) : (
            searchTerm && (
              <li
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                  height: '40px',
                  fontWeight: '400', 
                  fontSize: '14px',
                  color: '#999',
                }}
              >
                검색 결과가 없습니다.
              </li>
            )
          )}
        </ul>
      </div>
    </div>
  );
};

export default LabeledSelectBoxAuto;
